import { z } from "zod/v4";

export const crosswalkRuleModeSchema = z.enum([
  "direct",
  "fallback",
  "mixed",
  "branch-to-descendant",
]);

const categoryKeySchema = z.string().min(1);
const nonEmptyStringArraySchema = z.array(z.string().min(1)).min(1);

const normalizedSourceIdSchema = z
  .string()
  .min(1)
  .refine(
    (sourceId) => sourceId === sourceId.trim().toLowerCase().replace(/\s+/g, " "),
    "sourceId must already be normalized",
  );

const sourceIdPrefixesBySource: Record<string, string> = {
  "openfoodfacts-us-categories-tags": "off:category:",
  "usda-branded-food-category": "usda-branded:retail-family:",
  "usda-survey-wweia-food-category": "usda-survey:wweia-family:",
  "usda-sr-foundation-food-category": "usda-sr-foundation:food-category:",
};

const textRefinementRuleSchema = z
  .object({
    sourceId: normalizedSourceIdSchema.optional(),
    sourceIds: z.array(normalizedSourceIdSchema).min(1).optional(),
    matchWhenOnlySourceIds: z.array(normalizedSourceIdSchema).min(1).optional(),
    matchWhenNoSourceIds: z.boolean().optional(),
    categoryKey: categoryKeySchema,
    reason: z.string().min(1),
    phrases: nonEmptyStringArraySchema.optional(),
    allTokens: nonEmptyStringArraySchema.optional(),
    anyTokens: nonEmptyStringArraySchema.optional(),
    noneTokens: nonEmptyStringArraySchema.optional(),
  })
  .strict();

export const sourceCrosswalkRuleSchema = z
  .object({
    sourceId: normalizedSourceIdSchema,
    mode: crosswalkRuleModeSchema,
    categoryKey: categoryKeySchema.optional(),
    fallbackCategoryKey: categoryKeySchema.optional(),
    preferredCategoryKeys: nonEmptyStringArraySchema.optional(),
    blockedByCategoryKeys: nonEmptyStringArraySchema.optional(),
    requiresSupport: nonEmptyStringArraySchema.optional(),
  })
  .strict()
  .superRefine((rule, ctx) => {
    if (rule.mode === "direct" || rule.mode === "fallback") {
      if (!rule.categoryKey) {
        ctx.addIssue({
          code: "custom",
          message: `mode '${rule.mode}' requires categoryKey`,
          path: ["categoryKey"],
        });
      }

      for (const field of [
        "preferredCategoryKeys",
        "blockedByCategoryKeys",
        "requiresSupport",
      ] as const) {
        if (rule[field] !== undefined) {
          ctx.addIssue({
            code: "custom",
            message: `mode '${rule.mode}' must not include ${field}`,
            path: [field],
          });
        }
      }
    }

    if (rule.mode === "mixed" || rule.mode === "branch-to-descendant") {
      if (!rule.categoryKey && !rule.fallbackCategoryKey) {
        ctx.addIssue({
          code: "custom",
          message: `mode '${rule.mode}' requires categoryKey or fallbackCategoryKey`,
          path: ["fallbackCategoryKey"],
        });
      }
    }

    if (rule.mode === "branch-to-descendant" && !rule.preferredCategoryKeys?.length) {
      ctx.addIssue({
        code: "custom",
        message: "mode 'branch-to-descendant' requires preferredCategoryKeys",
        path: ["preferredCategoryKeys"],
      });
    }

    if (
      rule.blockedByCategoryKeys &&
      rule.mode !== "mixed" &&
      rule.mode !== "branch-to-descendant"
    ) {
      ctx.addIssue({
        code: "custom",
        message: "blockedByCategoryKeys requires mode 'mixed' or 'branch-to-descendant'",
        path: ["blockedByCategoryKeys"],
      });
    }

    if (rule.requiresSupport && rule.mode !== "mixed" && rule.mode !== "branch-to-descendant") {
      ctx.addIssue({
        code: "custom",
        message: "requiresSupport requires mode 'mixed' or 'branch-to-descendant'",
        path: ["requiresSupport"],
      });
    }
  });

function validateUniqueSourceIds(
  rules: readonly { sourceId: string }[],
  ctx: z.RefinementCtx,
): void {
  const seen = new Map<string, number>();
  rules.forEach((rule, index) => {
    const firstIndex = seen.get(rule.sourceId);
    if (firstIndex !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: `duplicate sourceId '${rule.sourceId}' first appears at rules[${firstIndex}]`,
        path: ["rules", index, "sourceId"],
      });
      return;
    }

    seen.set(rule.sourceId, index);
  });
}

function validateSourceIdNamespace(
  source: string,
  sourceId: string,
  path: (string | number)[],
  ctx: z.RefinementCtx,
): void {
  const prefix = sourceIdPrefixesBySource[source];
  if (!prefix) {
    ctx.addIssue({
      code: "custom",
      message: `unknown crosswalk source '${source}'`,
      path: ["source"],
    });
    return;
  }

  if (sourceId.startsWith(prefix)) return;

  ctx.addIssue({
    code: "custom",
    message: `sourceId for source '${source}' must start with '${prefix}'`,
    path,
  });
}

function validateKnownCrosswalkSource(source: string, ctx: z.RefinementCtx): void {
  if (sourceIdPrefixesBySource[source]) return;

  ctx.addIssue({
    code: "custom",
    message: `unknown crosswalk source '${source}'`,
    path: ["source"],
  });
}

function validateSourceIdsNamespace(
  source: string,
  sourceIds: readonly string[] | undefined,
  path: (string | number)[],
  ctx: z.RefinementCtx,
): void {
  sourceIds?.forEach((sourceId, index) =>
    validateSourceIdNamespace(source, sourceId, [...path, index], ctx),
  );
}

function validateCrosswalkSourceIdNamespaces(
  crosswalk: {
    source: string;
    ignoreSourceIds?: readonly string[];
    highRiskOverrideSourceIds: readonly string[];
    rules: readonly { sourceId: string }[];
    textRefinementRules?: readonly {
      sourceId?: string;
      sourceIds?: readonly string[];
      matchWhenOnlySourceIds?: readonly string[];
    }[];
  },
  ctx: z.RefinementCtx,
): void {
  validateKnownCrosswalkSource(crosswalk.source, ctx);
  validateSourceIdsNamespace(crosswalk.source, crosswalk.ignoreSourceIds, ["ignoreSourceIds"], ctx);
  validateSourceIdsNamespace(
    crosswalk.source,
    crosswalk.highRiskOverrideSourceIds,
    ["highRiskOverrideSourceIds"],
    ctx,
  );
  crosswalk.rules.forEach((rule, index) => {
    validateSourceIdNamespace(crosswalk.source, rule.sourceId, ["rules", index, "sourceId"], ctx);
  });
  crosswalk.textRefinementRules?.forEach((rule, index) => {
    if (rule.sourceId) {
      validateSourceIdNamespace(
        crosswalk.source,
        rule.sourceId,
        ["textRefinementRules", index, "sourceId"],
        ctx,
      );
    }
    validateSourceIdsNamespace(
      crosswalk.source,
      rule.sourceIds,
      ["textRefinementRules", index, "sourceIds"],
      ctx,
    );
    validateSourceIdsNamespace(
      crosswalk.source,
      rule.matchWhenOnlySourceIds,
      ["textRefinementRules", index, "matchWhenOnlySourceIds"],
      ctx,
    );
  });
}

function validateKnownSourceIdReferences(
  crosswalk: {
    ignoreSourceIds?: readonly string[];
    highRiskOverrideSourceIds: readonly string[];
    rules: readonly { sourceId: string }[];
    textRefinementRules?: readonly {
      sourceId?: string;
      sourceIds?: readonly string[];
      matchWhenOnlySourceIds?: readonly string[];
    }[];
  },
  ctx: z.RefinementCtx,
): void {
  const knownSourceIds = new Set([
    ...(crosswalk.ignoreSourceIds ?? []),
    ...crosswalk.highRiskOverrideSourceIds,
    ...crosswalk.rules.map((rule) => rule.sourceId),
  ]);

  crosswalk.textRefinementRules?.forEach((rule, index) => {
    if (rule.sourceId && !knownSourceIds.has(rule.sourceId)) {
      ctx.addIssue({
        code: "custom",
        message: `unknown text refinement sourceId '${rule.sourceId}'`,
        path: ["textRefinementRules", index, "sourceId"],
      });
    }

    rule.sourceIds?.forEach((sourceId, sourceIdIndex) => {
      if (knownSourceIds.has(sourceId)) return;
      ctx.addIssue({
        code: "custom",
        message: `unknown text refinement sourceId '${sourceId}'`,
        path: ["textRefinementRules", index, "sourceIds", sourceIdIndex],
      });
    });

    rule.matchWhenOnlySourceIds?.forEach((sourceId, sourceIdIndex) => {
      if (knownSourceIds.has(sourceId)) return;
      ctx.addIssue({
        code: "custom",
        message: `unknown text refinement sourceId '${sourceId}'`,
        path: ["textRefinementRules", index, "matchWhenOnlySourceIds", sourceIdIndex],
      });
    });
  });
}

export const sourceTagCrosswalkSchema = z
  .object({
    source: z.string().min(1),
    taxonomyVersion: z.string().min(1),
    status: z.string().min(1),
    ignoreSourceIds: z.array(normalizedSourceIdSchema),
    highRiskOverrideSourceIds: z.array(normalizedSourceIdSchema),
    rules: z.array(sourceCrosswalkRuleSchema),
    textRefinementRules: z.array(textRefinementRuleSchema).optional(),
  })
  .strict()
  .superRefine((crosswalk, ctx) => {
    validateUniqueSourceIds(crosswalk.rules, ctx);
    validateCrosswalkSourceIdNamespaces(crosswalk, ctx);
    validateKnownSourceIdReferences(crosswalk, ctx);
  });

export const sourceFamilyCrosswalkSchema = z
  .object({
    source: z.string().min(1),
    taxonomyVersion: z.string().min(1),
    status: z.string().min(1),
    highRiskOverrideSourceIds: z.array(normalizedSourceIdSchema),
    rules: z.array(sourceCrosswalkRuleSchema),
    textRefinementRules: z.array(textRefinementRuleSchema).optional(),
  })
  .strict()
  .superRefine((crosswalk, ctx) => {
    validateUniqueSourceIds(crosswalk.rules, ctx);
    validateCrosswalkSourceIdNamespaces(crosswalk, ctx);
    validateKnownSourceIdReferences(crosswalk, ctx);
  });

export type SourceTagCrosswalk = z.infer<typeof sourceTagCrosswalkSchema>;
export type SourceTagCrosswalkRule = z.infer<typeof sourceCrosswalkRuleSchema>;
export type SourceFamilyCrosswalk = z.infer<typeof sourceFamilyCrosswalkSchema>;
export type SourceFamilyCrosswalkRule = z.infer<typeof sourceCrosswalkRuleSchema>;
